import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
} from '@mui/material';
import {
  ArrowBack,
  Security,
  Gavel,
  Shield,
  Assignment,
  AccountBalance,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ClubAgreementPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const sections = [
    {
      icon: <Assignment />,
      title: "Service Description",
      content: [
        "Digital member registration and management",
        "Membership card generation with QR codes",
        "Insurance documentation handling",
        "Event and shuttle service coordination",
        "Analytics and reporting tools"
      ]
    },
    {
      icon: <Shield />,
      title: "Data Controller Relationship",
      content: [
        "HFSS acts as sole data controller under GDPR",
        "Clubs are authorized users, not independent controllers",
        "All data decisions made centrally by platform",
        "Clubs have limited operational access only"
      ]
    },
    {
      icon: <Security />,
      title: "Security & Compliance",
      content: [
        "End-to-end encryption for all data",
        "Regular security audits and monitoring",
        "Immediate breach notification required",
        "Annual access reviews mandatory"
      ]
    },
    {
      icon: <Gavel />,
      title: "Legal Framework",
      content: [
        "Governed by Swiss law",
        "GDPR compliant processing",
        "Clear liability boundaries",
        "30-day termination notice"
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', py: 4 }}>
      <Container maxWidth="lg">
        <Paper 
          sx={{ 
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/')}
              sx={{
                mb: 3,
                color: '#764ba2',
                '&:hover': {
                  backgroundColor: 'rgba(118, 75, 162, 0.1)',
                },
              }}
            >
              Back to Home
            </Button>
            
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #3388ff 30%, #ff00fb 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                }}
              >
                Club Operating Agreement
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip label="Version 1.0" color="primary" />
                <Chip label="Effective: January 2025" color="secondary" />
                <Chip label="GDPR Compliant" color="success" />
              </Box>
            </Box>
          </Box>

          {/* Important Notice */}
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              For Club Administrators
            </Typography>
            <Typography variant="body2">
              This agreement governs how clubs use the HFSS platform to manage their members. 
              By using our services, clubs agree to these terms which ensure GDPR compliance and data protection.
            </Typography>
          </Alert>

          {/* Key Sections Grid */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3,
              mb: 4,
            }}
          >
            {sections.map((section, index) => (
              <Paper
                key={index}
                sx={{
                  p: 3,
                  border: '1px solid rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: '#3388ff', mr: 2 }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {section.title}
                  </Typography>
                </Box>
                <List dense>
                  {section.content.map((item, idx) => (
                    <ListItem key={idx} sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`• ${item}`}
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          color: 'text.secondary' 
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ))}
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Key Points */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Key Agreement Points
            </Typography>
            
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                <Shield sx={{ verticalAlign: 'middle', mr: 1 }} />
                Data Controller Status
              </Typography>
              <Typography variant="body1">
                HFSS (Simone Severini) is the sole data controller. Clubs do not have independent 
                data controller status and cannot make unilateral decisions about member data processing.
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, bgcolor: 'secondary.50' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'secondary.main' }}>
                <Security sx={{ verticalAlign: 'middle', mr: 1 }} />
                Security Obligations
              </Typography>
              <Typography variant="body1">
                Clubs must use strong passwords, enable 2FA when available, limit access to authorized 
                personnel, and immediately report any security incidents or breaches.
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                <Assignment sx={{ verticalAlign: 'middle', mr: 1 }} />
                Member Rights
              </Typography>
              <Typography variant="body1">
                All GDPR member rights (access, rectification, erasure, portability) are handled 
                centrally through the platform. Clubs must forward any requests to privacy@hikeandfly.app.
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: 'warning.50' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
                <AccountBalance sx={{ verticalAlign: 'middle', mr: 1 }} />
                Legal Framework
              </Typography>
              <Typography variant="body1">
                This agreement is governed by Swiss law. Data retention follows legal requirements: 
                2 years for former members, 10 years for financial records, 5 years for safety records.
              </Typography>
            </Paper>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Contact Information */}
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Contact Information
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data Protection
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  privacy@hikeandfly.app
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Club Support
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  clubs@hikeandfly.app
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Action Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={() => navigate('/register')}
              size="large"
              sx={{
                background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                },
              }}
            >
              Register Your Club
            </Button>
          </Box>

          {/* Footer Note */}
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              textAlign: 'center', 
              mt: 4, 
              color: 'text.secondary' 
            }}
          >
            Acceptance of this agreement is recorded electronically with timestamp and IP address.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default ClubAgreementPage;